import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, LinearProgress,
  FormControl, InputLabel, Select, MenuItem, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Collapse, Link,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SpeedIcon from '@mui/icons-material/Speed';
import PsychologyIcon from '@mui/icons-material/Psychology';
import EditRoadIcon from '@mui/icons-material/EditRoad';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import SportsMotorsportsIcon from '@mui/icons-material/SportsMotorsports';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';

import { collection, query, where, getDocs, addDoc, orderBy } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const NEW_TRACK_ID = '__new__';

// ── Drop zone singola ────────────────────────────────────────────
function DropZone({ id, label, hint, file, required, onChange, onRemove }) {
  const [over, setOver] = useState(false);
  const handle = (f) => { if (f) onChange(f); };

  const handleRemove = (e) => {
    e.stopPropagation();
    // Reset l'input file così si può ricaricare lo stesso file
    const input = document.getElementById(id);
    if (input) input.value = '';
    onRemove?.();
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        onDrop={e => { e.preventDefault(); setOver(false); handle(e.dataTransfer.files[0]); }}
        onDragOver={e => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onClick={() => document.getElementById(id).click()}
        sx={{
          border: '2px dashed',
          borderColor: over ? 'secondary.main' : file ? 'success.main' : 'divider',
          borderRadius: 2, p: 3, minHeight: 110,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 0.5, cursor: 'pointer', transition: 'all 0.15s',
          bgcolor: over ? 'rgba(63,81,181,0.05)' : 'transparent',
          '&:hover': { borderColor: file ? 'success.main' : 'secondary.main', bgcolor: 'rgba(63,81,181,0.03)' },
        }}
      >
        <input id={id} type="file" accept=".csv" style={{ display: 'none' }}
          onChange={e => handle(e.target.files[0])} />
        {file ? (
          <>
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="caption" fontWeight={600} sx={{ textAlign: 'center', wordBreak: 'break-all', lineHeight: 1.3 }}>
              {file.name}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
              {(file.size / 1024).toFixed(0)} KB · clicca per sostituire
            </Typography>
          </>
        ) : (
          <>
            <UploadFileIcon sx={{ fontSize: 20, color: required ? 'secondary.main' : 'text.disabled' }} />
            <Typography variant="caption" fontWeight={required ? 700 : 500}
              color={required ? 'text.primary' : 'text.secondary'} sx={{ textAlign: 'center' }}>
              {label}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10 }}>
              {hint}
            </Typography>
          </>
        )}
      </Box>

      {/* Pulsante × per rimuovere il file */}
      {file && onRemove && (
        <IconButton
          size="small"
          onClick={handleRemove}
          sx={{
            position: 'absolute', top: -8, right: -8,
            width: 22, height: 22,
            bgcolor: 'error.main', color: 'white',
            '&:hover': { bgcolor: 'error.dark' },
            zIndex: 1,
          }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </IconButton>
      )}
    </Box>
  );
}

// ── Dialog Nuova Pista ───────────────────────────────────────────
function NewTrackDialog({ open, onClose, onSave, teamId }) {
  const { t } = useTranslation('race_engineer');
  const [nome, setNome]     = useState('');
  const [lungh, setLungh]   = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!nome.trim()) return;
    setSaving(true);
    try {
      const ref = await addDoc(collection(db, 'tracks'), {
        teamId,
        nome:          nome.trim(),
        lengthMeters:  Number(lungh) || null,
        address:       '',
        contacts:      '',
        corners:       [],
      });
      onSave({ id: ref.id, nome: nome.trim(), lengthMeters: Number(lungh) || null });
      setNome(''); setLungh('');
    } catch (e) {
      console.error('Errore creazione pista:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={700}>{t('setup.new_track_title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label={t('setup.track_name_label')} fullWidth size="small" value={nome}
            onChange={e => setNome(e.target.value)} autoFocus />
          <TextField label={t('setup.track_length_label')} fullWidth size="small" type="number"
            value={lungh} onChange={e => setLungh(e.target.value)} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('setup.cancel')}</Button>
        <Button variant="contained" disabled={!nome.trim() || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
          onClick={handleSave}>
          {t('setup.create_track')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Componente principale ────────────────────────────────────────
export default function SessionSetup({ teamId, onLoad, onTrackSelect, onCoachGuida, onCoachSetup, isLoading, isCoachingGuida, isCoachingSetup, hasData }) {
  const { t } = useTranslation('race_engineer');
  // File mia sessione
  const [csv, setCsv]           = useState(null);
  const [split, setSplit]       = useState(null);
  // File riferimento
  const [refCsv, setRefCsv]     = useState(null);
  const [refSplit, setRefSplit] = useState(null);

  // Info sessione
  const [tracks, setTracks]       = useState([]);
  const [vehicles, setVehicles]   = useState([]);
  const [drivers, setDrivers]     = useState([]);
  const [selTrack, setSelTrack]   = useState('');
  const [selDriver, setSelDriver] = useState('');
  const [newTrackOpen, setNewTrackOpen] = useState(false);
  // Setup A (sessione principale)
  const [selVehicleA, setSelVehicleA] = useState('');
  const [selSetupA, setSelSetupA]     = useState('');
  const [setupsA, setSetupsA]         = useState([]);
  // Setup B (sessione riferimento)
  const [selVehicleB, setSelVehicleB] = useState('');
  const [selSetupB, setSelSetupB]     = useState('');
  const [setupsB, setSetupsB]         = useState([]);

  // Carica piste, veicoli e piloti del team
  useEffect(() => {
    if (!teamId) return;
    getDocs(query(collection(db, 'tracks'), where('teamId', '==', teamId), orderBy('nome', 'asc')))
      .then(s => setTracks(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
    getDocs(query(collection(db, 'vehicles'), where('teamId', '==', teamId)))
      .then(s => setVehicles(s.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.numeroGara || '').localeCompare(b.numeroGara || ''))))
      .catch(() => {});
    getDocs(query(collection(db, 'drivers'), where('teamId', '==', teamId), orderBy('nomeCompleto', 'asc')))
      .then(s => setDrivers(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [teamId]);

  // Carica setup A quando cambia veicolo A
  useEffect(() => {
    if (!teamId || !selVehicleA) { setSetupsA([]); return; }
    getDocs(query(collection(db, 'setupSheets'), where('teamId', '==', teamId),
      where('vehicleId', '==', selVehicleA), orderBy('createdAt', 'desc')))
      .then(s => setSetupsA(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [teamId, selVehicleA]);

  // Carica setup B quando cambia veicolo B
  useEffect(() => {
    if (!teamId || !selVehicleB) { setSetupsB([]); return; }
    getDocs(query(collection(db, 'setupSheets'), where('teamId', '==', teamId),
      where('vehicleId', '==', selVehicleB), orderBy('createdAt', 'desc')))
      .then(s => setSetupsB(s.docs.map(d => ({ id: d.id, ...d.data() }))))
      .catch(() => {});
  }, [teamId, selVehicleB]);

  const handleTrackChange = (val) => {
    if (val === NEW_TRACK_ID) { setNewTrackOpen(true); return; }
    setSelTrack(val);
    const track = tracks.find(t => t.id === val) || null;
    if (onTrackSelect) onTrackSelect(track);
  };

  const handleNewTrackSaved = (track) => {
    setTracks(prev => [...prev, track].sort((a, b) => a.nome.localeCompare(b.nome)));
    setSelTrack(track.id);
    setNewTrackOpen(false);
  };

  const vehicleLabel = v => [v.marca, v.modello, v.numeroGara ? `#${v.numeroGara}` : null].filter(Boolean).join(' ');

  const setupLabel = s => {
    let date = '';
    try { date = (s.dataSessione?.toDate ? s.dataSessione.toDate() : new Date(s.dataSessione)).toLocaleDateString('it-IT'); } catch {}
    return [date, s.sessionName, s.trackName].filter(Boolean).join(' — ') || 'Setup';
  };

  const selectedTrack    = tracks.find(t => t.id === selTrack)     || null;
  const selectedVehicleA = vehicles.find(v => v.id === selVehicleA) || null;
  const selectedSetupA   = setupsA.find(s => s.id === selSetupA)   || null;
  const selectedVehicleB = vehicles.find(v => v.id === selVehicleB) || null;
  const selectedSetupB   = setupsB.find(s => s.id === selSetupB)   || null;
  const selectedDriver   = drivers.find(d => d.id === selDriver)   || null;

  const driverLabel = d => d.nomeCompleto || d.id;

  // Toggle "Opzioni avanzate" per mostrare i campi Split Report (ora opzionali).
  // Si apre automaticamente se l'utente ha già caricato uno dei due split.
  const [advancedOpen, setAdvancedOpen] = useState(!!(split || refSplit));

  const doLoad = useCallback(() => {
    onLoad({
      csv, splitReport: split,
      referenceCsv: refCsv, referenceSplit: refSplit,
      track:    selectedTrack,
      vehicle:  selectedVehicleA,
      driver:   selectedDriver,
      setupA:   selectedSetupA,
      setupB:   selectedSetupB,
    });
  }, [csv, split, refCsv, refSplit, selectedTrack, selectedVehicleA, selectedDriver, selectedSetupA, selectedSetupB, onLoad]);

  // Split Report ora opzionale: i settori vengono identificati automaticamente
  // dal backend (detect_corners) se non viene fornito.
  const handleLoad = useCallback(() => {
    if (!csv) return;
    doLoad();
  }, [csv, doLoad]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

      {/* ── Due riquadri affiancati: CSS Grid, non MUI Grid ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Riquadro: La mia sessione */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SpeedIcon sx={{ color: 'secondary.main', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>{t('setup.session_main')}</Typography>
            <Chip label={t('setup.aim_chip')} size="small" variant="outlined" sx={{ fontSize: 10, ml: 'auto' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DropZone id="re-csv"   label={t('setup.csv_telemetry')} hint={t('setup.required_hint')}      file={csv}   required onChange={setCsv}   onRemove={() => setCsv(null)} />
          </Box>
        </Paper>

        {/* Riquadro: Sessione Riferimento */}
        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SpeedIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700} color="text.secondary">{t('setup.session_reference')}</Typography>
            <Chip label={t('setup.optional_chip')} size="small" variant="outlined"
              sx={{ fontSize: 10, ml: 'auto', color: 'text.disabled', borderColor: 'divider' }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <DropZone id="re-refcsv"   label={t('setup.csv_reference')}    hint={t('setup.pole_hint')}         file={refCsv}   onChange={setRefCsv}   onRemove={() => setRefCsv(null)} />
          </Box>
        </Paper>
      </Box>

      {/* ── Opzioni avanzate: Split Report (opzionali, ora i settori sono automatici) ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Link
          component="button"
          variant="caption"
          onClick={() => setAdvancedOpen(o => !o)}
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
        >
          {advancedOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          {t('setup.advanced_options')}
        </Link>
        <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
          · {t('setup.auto_sectors_hint')}
        </Typography>
      </Box>
      <Collapse in={advancedOpen} timeout="auto">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <Paper elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', p: 2 }}>
            <DropZone id="re-split" label={t('setup.split_report')} hint={t('setup.sectors_precise_hint')} file={split} onChange={setSplit} onRemove={() => setSplit(null)} />
          </Paper>
          <Paper elevation={0} sx={{ border: '1px dashed', borderColor: 'divider', p: 2 }}>
            <DropZone id="re-refsplit" label={t('setup.split_reference')} hint={t('setup.max_precision_hint')} file={refSplit} onChange={setRefSplit} onRemove={() => setRefSplit(null)} />
          </Paper>
        </Box>
      </Collapse>

      {/* ── Riquadro info + pulsante ── */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}>

      {/* ── Info sessione ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>

        {/* Pilota */}
        <FormControl size="small" fullWidth>
          <InputLabel>{t('setup.driver_label')}</InputLabel>
          <Select value={selDriver} label={t('setup.driver_label')} onChange={e => setSelDriver(e.target.value)}
            startAdornment={<SportsMotorsportsIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />}>
            <MenuItem value=""><em>{t('setup.not_specified')}</em></MenuItem>
            {drivers.map(d => <MenuItem key={d.id} value={d.id}>{driverLabel(d)}</MenuItem>)}
          </Select>
        </FormControl>

        <FormControl size="small" fullWidth>
          <InputLabel>{t('setup.track_label')}</InputLabel>
          <Select value={selTrack} label={t('setup.track_label')} onChange={e => handleTrackChange(e.target.value)}
            startAdornment={<EditRoadIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />}>
            <MenuItem value=""><em>{t('setup.not_specified_f')}</em></MenuItem>
            {tracks.map(tr => <MenuItem key={tr.id} value={tr.id}>{tr.nome}</MenuItem>)}
            <Divider />
            <MenuItem value={NEW_TRACK_ID}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'secondary.main' }}>
                <AddIcon fontSize="small" /> {t('setup.new_track')}
              </Box>
            </MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
          {/* ── Setup A — sessione principale ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} color="secondary.main"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DirectionsCarIcon sx={{ fontSize: 13 }} /> {t('setup.setup_a_label')}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('setup.vehicle_a_label')}</InputLabel>
              <Select value={selVehicleA} label={t('setup.vehicle_a_label')}
                onChange={e => { setSelVehicleA(e.target.value); setSelSetupA(''); }}>
                <MenuItem value=""><em>{t('setup.not_spec_short')}</em></MenuItem>
                {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{vehicleLabel(v)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth disabled={!selVehicleA}>
              <InputLabel>{t('setup.setup_a_label').split(' — ')[0]}</InputLabel>
              <Select value={selSetupA} label={t('setup.setup_a_label').split(' — ')[0]} onChange={e => setSelSetupA(e.target.value)}>
                <MenuItem value=""><em>{t('setup.not_spec_short')}</em></MenuItem>
                {setupsA.map(s => <MenuItem key={s.id} value={s.id}>{setupLabel(s)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* ── Setup B — sessione riferimento ── */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DirectionsCarIcon sx={{ fontSize: 13 }} /> {t('setup.setup_b_label')}
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel>{t('setup.vehicle_b_label')}</InputLabel>
              <Select value={selVehicleB} label={t('setup.vehicle_b_label')}
                onChange={e => { setSelVehicleB(e.target.value); setSelSetupB(''); }}>
                <MenuItem value=""><em>{t('setup.not_spec_short')}</em></MenuItem>
                {vehicles.map(v => <MenuItem key={v.id} value={v.id}>{vehicleLabel(v)}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth disabled={!selVehicleB}>
              <InputLabel>{t('setup.setup_b_label').split(' — ')[0]}</InputLabel>
              <Select value={selSetupB} label={t('setup.setup_b_label').split(' — ')[0]} onChange={e => setSelSetupB(e.target.value)}>
                <MenuItem value=""><em>{t('setup.not_spec_short')}</em></MenuItem>
                {setupsB.map(s => <MenuItem key={s.id} value={s.id}>{setupLabel(s)}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Box>

      {/* ── Progress bar ── */}
      {(isLoading || isCoachingGuida || isCoachingSetup) && (
        <LinearProgress color="secondary" sx={{ mb: 1.5, borderRadius: 1 }} />
      )}

      {/* ── Bottoni ── */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

        {/* Carica Sessione — sempre visibile se c'è un CSV */}
        {csv && (
          <Button
            variant={hasData ? 'outlined' : 'contained'}
            fullWidth size="large"
            disabled={isLoading}
            onClick={handleLoad}
            startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : <SpeedIcon />}
            sx={{ height: hasData ? 40 : 48 }}>
            {isLoading ? t('setup.loading') : hasData ? t('setup.reload_csv') : t('setup.load_session')}
          </Button>
        )}

        {/* Coaching buttons — solo dopo aver caricato */}
        {hasData && (
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <Button variant="contained" size="large"
              disabled={isCoachingGuida || isCoachingSetup}
              onClick={onCoachGuida}
              startIcon={isCoachingGuida ? <CircularProgress size={16} color="inherit" /> : <SportsMotorsportsIcon />}
              sx={{ height: 48 }}>
              {isCoachingGuida ? t('setup.analyzing') : t('setup.driving_tips')}
            </Button>
            <Button variant="contained" size="large" color="secondary"
              disabled={isCoachingGuida || isCoachingSetup || (!selectedSetupA && !selectedSetupB)}
              onClick={onCoachSetup}
              startIcon={isCoachingSetup ? <CircularProgress size={16} color="inherit" /> : <BuildIcon />}
              title={!selectedSetupA && !selectedSetupB ? t('setup.select_setup_required') : ''}
              sx={{ height: 48 }}>
              {isCoachingSetup ? t('setup.analyzing') : t('setup.setup_tips')}
            </Button>
          </Box>
        )}

        {/* Messaggio se non c'è CSV dopo aver caricato */}
        {!csv && !hasData && (
          <Button variant="contained" fullWidth size="large" disabled sx={{ height: 48 }}>
            {t('setup.load_csv_to_start')}
          </Button>
        )}
      </Box>

      <NewTrackDialog
        open={newTrackOpen}
        onClose={() => setNewTrackOpen(false)}
        onSave={handleNewTrackSaved}
        teamId={teamId}
      />


    </Paper>
    </Box>
  );
}
